/**
 * Account Service
 * Profile management operations for the current user
 */

import { apiClient } from '../client';

// Types

export interface ProfileDto {
  userName: string;
  email: string;
  name?: string;
  surname?: string;
  phoneNumber?: string;
  isExternal?: boolean;
  hasPassword?: boolean;
  concurrencyStamp?: string;
}

export interface UpdateProfileInput {
  userName?: string;
  email?: string;
  name?: string;
  surname?: string;
  phoneNumber?: string;
  concurrencyStamp?: string;
}

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export interface AccountSettingsDto {
  isUserNameUpdateEnabled: boolean;
  isEmailUpdateEnabled: boolean;
}

export interface ProfilePictureDto {
  type: number;
  source: string;
  fileContent?: string;
}

// Service

export const accountService = {
  getMyProfile: async (): Promise<ProfileDto> => {
    const response = await apiClient.get<ProfileDto>('/api/account/my-profile');
    return response.data;
  },

  updateMyProfile: async (input: UpdateProfileInput): Promise<ProfileDto> => {
    const response = await apiClient.put<ProfileDto>('/api/account/my-profile', input);
    return response.data;
  },

  changePassword: async (input: ChangePasswordInput): Promise<void> => {
    await apiClient.post('/api/account/my-profile/change-password', input);
  },

  getAccountSettings: async (): Promise<AccountSettingsDto> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await apiClient.get<any>('/api/abp/application-configuration');
    const settings = response.data?.setting?.values || {};
    return {
      isUserNameUpdateEnabled: settings['Abp.Identity.User.IsUserNameUpdateEnabled'] !== 'false',
      isEmailUpdateEnabled: settings['Abp.Identity.User.IsEmailUpdateEnabled'] !== 'false',
    };
  },

  getProfilePicture: async (userId: string): Promise<ProfilePictureDto> => {
    const response = await apiClient.get<ProfilePictureDto>(`/api/account/profile-picture/${userId}`);
    return response.data;
  },

  uploadProfilePicture: async (type: number, file?: File): Promise<void> => {
    if (type === 2 && file) {
      const formData = new FormData();
      formData.append('ImageContent', file);
      formData.append('Type', type.toString());
      await apiClient.post('/api/account/profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } else {
      await apiClient.post('/api/account/profile-picture', { type });
    }
  },
};
